"use client";
import { format } from "date-fns";
import { useEffect, useMemo, useState } from "react";
import { UseFormReturn, useForm } from "react-hook-form";
import { toast } from "react-hot-toast";
import {
  ALL_WEEK_DAYS,
  type HourQueryParams,
  type IHour,
  type IHourPost,
  type Office,
  type WeekDayNames,
  parseTimeOnly
} from "@gettactic/api";
import { type IResource, parseDateOnlyString } from "@gettactic/api";
import { api } from "@/lib/api/api";
import { cn } from "@gettactic/helpers/src/css/cn";
import { toEntity } from "@/lib/utils";
import { DEFAULT_WEIGHT_RULE } from "@/lib/validation/hour";
import { RESOURCES_TEXT } from "@/features/settings/utils/ListResources";
import { useCustomParkingVerbiageOnWeb as useCustomParkingVerbiage } from "@/lib/hooks/useCustomParkingVerbiage";
import { capitalize, logError } from "@gettactic/helpers";
import { HookFormTextInput } from "@gettactic/components/src/form/TextInput";
import { HookFormCalendarInput } from "@gettactic/components/src/form/FormCalendarInput";
import { HookFormToggleInput } from "@gettactic/components/src/form/ToggleInput";
import { HookFormTimeSelectDate } from "@cmp-src/form/TimeSelect";
import { ToggleInput } from "@gettactic/components/src/form/ToggleInput";
import { CrudModal } from "@cmp-src/overlay/modals/CrudModal";
import { CrudModalProps } from "@gettactic/components/src/overlay/modals/CrudModal/CrudModal.types";
import { Stack } from "@gettactic/components/src/primitives/Stack";

type CreateOrEditHourRuleProps = {
  hours?: IHour[];
  office: Office;
  resources?: IResource[];
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
  invalidateQuery: () => Promise<void>;
} & Pick<
  CrudModalProps,
  "asDrawer" | "size" | "className" | "contentClassName"
>;

type FormDataShape = {
  name: string;
  days: WeekDayNames[];
  is_available: boolean;
  start_time: Date;
  end_time: Date;
  since?: Date;
  until?: Date;
};

function getDefaultValues(hour?: IHour): FormDataShape {
  if (hour) {
    return {
      name: hour.name ?? "",
      days: hour.days ?? [],
      is_available: hour.is_available,
      start_time: parseTimeOnly(hour.start_time),
      end_time: parseTimeOnly(hour.end_time),
      since: hour.since ? parseDateOnlyString(hour.since) : undefined,
      until: hour.until ? parseDateOnlyString(hour.until) : undefined
    };
  }
  const startTime = parseTimeOnly("00:00:00.000");
  const endTime = parseTimeOnly("23:59:59.000");
  return {
    name: "",
    days: [...ALL_WEEK_DAYS],
    is_available: false,
    start_time: startTime,
    end_time: endTime,
    since: new Date(),
    until: new Date()
  };
}

export function is24Hours(start: Date, end: Date) {
  if (
    !start ||
    !end ||
    Number.isNaN(start.getTime()) ||
    Number.isNaN(end.getTime())
  ) {
    return false;
  }
  return (
    format(start, "hh:mm aa") === "12:00 AM" &&
    format(end, "hh:mm aa") === "11:59 PM"
  );
}

interface FormRegistration {
  form: UseFormReturn<FormDataShape>;
  onSubmit: () => Promise<void>;
  defaultValues: FormDataShape;
}

type ContentProps = {
  hours?: IHour[];
  office: Office;
  resources?: IResource[];
  invalidateQuery: () => Promise<void>;
  onClose: () => void;
  registerForm: (registration: FormRegistration) => void;
};

function CreateOrEditHourRuleContent({
  hours,
  office,
  resources,
  invalidateQuery,
  onClose,
  registerForm
}: ContentProps) {
  const defaultValues = getDefaultValues(hours ? hours[0] : undefined);

  const form = useForm<FormDataShape>({
    defaultValues,
    mode: "onSubmit"
  });

  const { watch, setValue, handleSubmit } = form;
  const start_time = watch("start_time");
  const end_time = watch("end_time");
  const advancedHours = !is24Hours(start_time, end_time);
  const isAvailable = watch("is_available");

  const customParkingVerbiage = useCustomParkingVerbiage();

  const resourceTypeText = useMemo(() => {
    const resourceType = resources?.[0]?.type;
    let text = "Resource";
    if (!resourceType) {
      return text;
    }
    const toEntityType = toEntity(resourceType);
    if (toEntityType) {
      text = RESOURCES_TEXT[toEntityType].individual;
    }

    const isParking = toEntityType === "parking";
    if (customParkingVerbiage && isParking) {
      text = capitalize(customParkingVerbiage);
    }
    return text;
  }, [resources?.[0]?.type, customParkingVerbiage]);

  const onSubmit = async () => {
    const data = form.getValues();
    if (!data.name || data.name.trim() === "") {
      toast.error("Name is required");
      return;
    }

    try {
      const body: IHourPost = {
        name: data.name,
        type: "rule",
        days: data.days,
        is_available: data.is_available,
        start_time: format(data.start_time, "HH:mm:ss"),
        end_time: format(data.end_time, "HH:mm:ss"),
        since: data.since
          ? `${format(data.since, "yyyy-MM-dd")}T00:00:00.000Z`
          : null,
        until: data.until
          ? `${format(data.until, "yyyy-MM-dd")}T23:59:59.000Z`
          : null,
        weight: DEFAULT_WEIGHT_RULE
      };
      const queryParams: HourQueryParams = {
        tz: office.time_zone
      };
      const res = hours
        ? await Promise.all(
            hours.map(async (hour) =>
              api.client.hours.put(office.id, hour.id, body, queryParams)
            )
          )
        : resources
          ? await Promise.all(
              resources.map(
                async (resource) =>
                  await api.client.hours.createForResource(
                    resource.id,
                    body,
                    queryParams
                  )
              )
            )
          : [await api.client.hours.create(office.id, body, queryParams)];

      if (res.find((x) => !!x.error)) {
        logError("Res: ", res);
        throw new Error("Error saving");
      }
      toast.success(hours ? "Rule updated" : "Rule created");
      onClose();
      invalidateQuery();
    } catch (e) {
      toast.error("Sorry we couldn't save the rule");
    }
  };

  // Register form with parent component
  useEffect(() => {
    registerForm({
      form,
      onSubmit: handleSubmit(onSubmit),
      defaultValues
    });
  }, [form]);

  return (
    <Stack>
      <div className="flex items-center justify-between">
        <div className="ml-1 text-base font-medium text-neutral-800">
          {resources
            ? `${resourceTypeText} Availability`
            : "Office Availability"}
        </div>
        <div className="flex items-center">
          <span className={cn("px-2", !isAvailable ? "font-bold" : "")}>
            Closed
          </span>
          <HookFormToggleInput form={form} name="is_available" size="sm" />
          <span className={cn("px-2", isAvailable ? "font-bold" : "")}>
            Open
          </span>
        </div>
      </div>

      <HookFormTextInput
        form={form}
        name="name"
        label="Name"
        placeholder="Ex. New Year's Day"
        isRequired
      />

      <div className="flex justify-between space-x-4">
        <div className="w-full">
          <HookFormCalendarInput
            form={form}
            name="since"
            label="Start Date"
            rules={{ required: true }}
            mode="single"
            variant="outlined"
          />
        </div>
        <div className="w-full">
          <HookFormCalendarInput
            form={form}
            name="until"
            label="End Date"
            rules={{ required: true }}
            mode="single"
            variant="outlined"
          />
        </div>
      </div>

      <div className="flex items-center gap-4">
        <ToggleInput
          checked={!advancedHours}
          onCheckedChange={(checked) => {
            if (checked) {
              setValue("start_time", parseTimeOnly("00:00:00.000"));
              setValue("end_time", parseTimeOnly("23:59:59.000"));
            } else {
              setValue("start_time", parseTimeOnly("09:00:00.000"));
              setValue("end_time", parseTimeOnly("17:00:00.000"));
            }
          }}
          togglePosition="left"
          label="All day"
        />
      </div>
      {advancedHours ? (
        <div className="flex flex-1 items-center">
          <HookFormTimeSelectDate
            form={form}
            name="start_time"
            rules={{ required: true }}
            placeholder="Start"
            interval={30}
          />
          <div className="mx-4 w-4 border-b border-neutral-300" />
          <HookFormTimeSelectDate
            form={form}
            name="end_time"
            rules={{ required: true }}
            placeholder="End"
            interval={30}
          />
        </div>
      ) : null}
    </Stack>
  );
}

export function CreateOrEditHourRule({
  office,
  resources,
  isOpen,
  onOpenChange,
  invalidateQuery,
  hours,
  asDrawer,
  size,
  className,
  contentClassName
}: CreateOrEditHourRuleProps) {
  const [formRegistration, setFormRegistration] =
    useState<FormRegistration | null>(null);

  const isEditing = !!hours;
  const title = resources ? "Custom Hours" : "Holidays & Closures";

  return (
    <CrudModal
      title={title}
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      form={formRegistration?.form}
      defaultValues={formRegistration?.defaultValues}
      isEditing={isEditing}
      onSubmit={formRegistration?.onSubmit}
      submitLabel="Save"
      asDrawer={asDrawer}
      size={size}
      className={className}
      contentClassName={contentClassName}
    >
      <CreateOrEditHourRuleContent
        hours={hours}
        office={office}
        resources={resources}
        invalidateQuery={invalidateQuery}
        registerForm={setFormRegistration}
        onClose={() => onOpenChange(false)}
      />
    </CrudModal>
  );
}
